import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import filesize from 'filesize';
import {FileText, Trash2Icon, Upload} from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';
import Dropzone from 'react-dropzone';

import {CREATE_FLOWS} from '../../../constants';
import {
  BLACK_100,
  DARK_TEXT_COLOR,
  ERROR_100,
  GREEN_BUTTON,
  GREY_BACKGROUND_HOVER,
  GREY_BORDER_LIGHTER,
  GREY_SUBTLE,
  GREY_TEXT_SECONDARY,
  WHITE_100,
} from '../../../theme';

const styles = {
  boldLabel: {
    color: DARK_TEXT_COLOR,
    fontSize: '0.875rem',
    fontWeight: '700',
    lineHeight: '140%',
  },
  container: {
    backgroundColor: GREY_BACKGROUND_HOVER,
    padding: '1.5rem',
  },
  deleteButton: {
    borderColor: GREY_BORDER_LIGHTER,
    borderRadius: '8px',
    height: '32px',
    minHeight: '32px',
    minWidth: '32px',
    padding: 0,
    width: '32px',
  },
  dropzone: (hasFile) => ({
    backgroundColor: hasFile ? WHITE_100 : 'initial',
    borderColor: GREY_BORDER_LIGHTER,
    borderRadius: '8px',
    borderStyle: hasFile ? 'solid' : 'dashed',
    borderWidth: hasFile ? 1 : 2,
    cursor: !hasFile ? 'pointer' : 'initial',
    height: '5rem',
    padding: '1rem',
  }),
  dropzoneLabel: {
    color: GREY_TEXT_SECONDARY,
    fontSize: '0.875rem',
    fontWeight: '400',
    lineHeight: '140%',
  },
  errorText: {
    color: ERROR_100,
    fontSize: '12px',
    fontWeight: 500,
  },
  fileName: {
    color: DARK_TEXT_COLOR,
    fontWeight: 600,
  },
  fileSize: {
    color: GREY_SUBTLE,
  },
};

const getFileToImportLabel = (createFlow) => {
  switch (createFlow) {
    case CREATE_FLOWS.pdfReviewAssistant:
      return 'Upload a PDF';
    case CREATE_FLOWS.upload:
      return 'File to import';
    default:
      return 'File to import and rollforward:';
  }
};

export const ImportFileDropzone = ({
  createFlow,
  hasDropError,
  isProcessing,
  onError,
  pdfFile,
  renderPriorPresentationFormat,
  renderSourceReportType,
  setHasDropError,
  setPdfFile,
}) => {
  if (
    (createFlow !== CREATE_FLOWS.pdf &&
      createFlow !== CREATE_FLOWS.upload &&
      createFlow !== CREATE_FLOWS.pdfReviewAssistant) ||
    isProcessing
  ) {
    return null;
  }

  const getLabel = (isDragActive) => {
    if (pdfFile) {
      return <Typography sx={styles.dropzoneLabel}>{pdfFile.name}</Typography>;
    } else if (isDragActive) {
      return (
        <Typography sx={styles.dropzoneLabel}>
          Drop the files here ...
        </Typography>
      );
    } else {
      return (
        <Typography sx={styles.dropzoneLabel}>
          Drag and drop PDF file here or{' '}
          <Box component="span" sx={{textDecoration: 'underline'}}>
            select file
          </Box>
        </Typography>
      );
    }
  };

  return (
    <Stack direction="column" sx={styles.container}>
      <Typography pb={1} style={styles.boldLabel}>
        {getFileToImportLabel(createFlow)}
      </Typography>
      <Dropzone
        accept={{'application/pdf': ['.pdf']}}
        maxFiles={1}
        onDrop={(acceptedFiles) => {
          const file = acceptedFiles[0];
          if (file && file.size === 0) {
            onError(
              'The selected file appears to be empty (0 bytes). Please select a valid PDF file.'
            );
            return;
          }
          setPdfFile(file);
        }}
        onDropAccepted={() => setHasDropError(false)}
        onDropRejected={() => setHasDropError(true)}
      >
        {({getInputProps, getRootProps, isDragActive}) => (
          <Stack pb={2}>
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="flex-start"
              spacing={2}
              sx={styles.dropzone(!!pdfFile)}
              {...getRootProps()}
            >
              {pdfFile ? (
                <>
                  <FileText color={GREEN_BUTTON} size={32} />
                  <Stack
                    direction="column"
                    height="100%"
                    justifyContent="center"
                    width="100%"
                  >
                    <Typography sx={styles.fileName}>{pdfFile.name}</Typography>
                    <Typography sx={styles.fileSize}>
                      PDF - {filesize(pdfFile.size, {standard: 'jedec'})}
                    </Typography>
                  </Stack>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPdfFile(null);
                    }}
                    sx={styles.deleteButton}
                    variant="outlined"
                  >
                    <Trash2Icon color={BLACK_100} size={16} />
                  </Button>
                </>
              ) : (
                <>
                  <input {...getInputProps()} />
                  <Upload size="1rem" />
                  {getLabel(isDragActive)}
                </>
              )}
            </Stack>
            {hasDropError && (
              <Typography mt={1.25} sx={styles.errorText}>
                Unsupported file type. Please import a PDF file.
              </Typography>
            )}
          </Stack>
        )}
      </Dropzone>
      {createFlow === CREATE_FLOWS.pdf && (
        <Stack spacing="1rem">
          {renderSourceReportType?.()}
          {renderPriorPresentationFormat?.()}
        </Stack>
      )}
    </Stack>
  );
};

ImportFileDropzone.propTypes = {
  createFlow: PropTypes.string,
  hasDropError: PropTypes.bool,
  isProcessing: PropTypes.bool.isRequired,
  onError: PropTypes.func.isRequired,
  pdfFile: PropTypes.object,
  renderPriorPresentationFormat: PropTypes.func,
  renderSourceReportType: PropTypes.func,
  setHasDropError: PropTypes.func.isRequired,
  setPdfFile: PropTypes.func.isRequired,
};
